import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  LocalShipping as ShippingIcon,
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  ContactMail as ContactIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const EnhancedReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchReturnDetails();
    }
  }, [id]);

  const fetchReturnDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/returns/${id}`);
      
      if (response.data.success) {
        const returnInfo = response.data.data;
        setReturnData(returnInfo);
        
        if (returnInfo.items) {
          setReturnItems(Array.isArray(returnInfo.items) ? returnInfo.items : []);
        } else if (returnInfo.items_returned) {
          try {
            const items = JSON.parse(returnInfo.items_returned);
            setReturnItems(Array.isArray(items) ? items : []);
          } catch (e) {
            console.error('Error parsing return items:', e);
            setReturnItems([]);
          }
        } else {
          setReturnItems([]);
        }
      } else {
        setError('Return not found');
      }
    } catch (error) {
      console.error('Error fetching return details:', error);
      setError('Failed to load return details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? 'Today' : `${diffDays} days ago`;
  };

  const getPlatform = () => {
    // Check for actual platform field first
    if (returnData?.platform) return returnData.platform;
    
    // Check return number prefix to determine platform
    if (returnData?.return_number?.startsWith('SC-')) return 'Sellercloud';
    if (returnData?.return_number?.startsWith('AMZ-')) return 'Amazon';
    if (returnData?.return_number?.startsWith('EB-')) return 'eBay';
    
    // Fallback to order ID checks
    if (returnData?.amazon_order_id) return 'Amazon';
    if (returnData?.sellercloud_order_id) return 'Sellercloud';
    if (returnData?.ebay_order_id) return 'eBay';
    
    return 'Sellercloud'; // Default to Sellercloud for SC- prefixed returns
  };

  const getMarketplaceFlag = () => {
    const platform = getPlatform();
    if (platform === 'Amazon') return '🇬🇧'; // UK flag for Amazon.co.uk
    if (platform === 'eBay') return '🇬🇧'; // UK flag for eBay.co.uk
    return '🌍'; // Global for others
  };

  const getOrderId = () => {
    const platform = getPlatform();
    
    // Return appropriate order ID based on platform
    if (platform === 'Amazon') {
      return returnData?.amazon_order_id || returnData?.order_id || 'N/A';
    } else if (platform === 'eBay') {
      return returnData?.ebay_order_id || returnData?.channel_order_ref || returnData?.order_id || 'N/A';
    } else if (platform === 'Sellercloud') {
      return returnData?.sellercloud_order_id || returnData?.order_id || 'N/A';
    }
    
    return returnData?.order_id || 'N/A';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !returnData) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/returns')}
          sx={{ mb: 2 }}
        >
          Back to Returns
        </Button>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Return not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/returns')}
          sx={{ mr: 2 }}
        >
          Back to Returns
        </Button>
        <img 
          src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
          alt="XS Stock"
          style={{
            height: '32px',
            marginRight: '12px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A365D' }}>
          Return Details - {returnData.return_number}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Order ID and Product Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Order ID:
                    </Typography>
                    <Link
                      sx={{ ml: 1, color: '#007185', cursor: 'pointer' }}
                      onClick={() => {
                        const platform = getPlatform().toLowerCase();
                        const orderId = getOrderId();
                        navigate(`/orders/${platform}/${orderId}`);
                      }}
                    >
                      {getOrderId()}
                    </Link>
                    <Chip 
                      label={getPlatform()}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Buyer: {returnData.customer_name || 'Unknown'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      Marketplace
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ mr: 1 }}>{getMarketplaceFlag()}</Typography>
                      <Typography variant="body2">United Kingdom</Typography>
                    </Box>
                  </Box>

                  <Link 
                    component="button"
                    variant="body2" 
                    sx={{ color: '#007185', textAlign: 'left' }}
                    onClick={() => {/* Communication history handler */}}
                  >
                    Communication history
                  </Link>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Request Date: {formatDate(returnData.created_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {getDaysAgo(returnData.created_at)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <Chip 
                      label="Auto-authorised"
                      color="success"
                      size="small"
                      variant="filled"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                    <Chip 
                      label={`Prior refund: ${returnData.return_status === 'processed' ? 'Completed' : 'Pending'}`}
                      color={returnData.return_status === 'processed' ? 'success' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Date: {formatDate(returnData.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {getDaysAgo(returnData.created_at)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Approval Date: {formatDate(returnData.updated_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getDaysAgo(returnData.updated_at)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Return Items */}
              {returnItems.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 3 }}>
                  <Avatar
                    src={item.image || '/placeholder-product.png'}
                    variant="square"
                    sx={{ width: 60, height: 60, mr: 2, bgcolor: '#f5f5f5' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                      {item.product_name || item.productName || item.title || 'Unknown Product'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Return Quantity: {item.quantity_to_return || item.returnQuantity || 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ASIN: {item.asin || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Merchant SKU: {item.sku || item.sellerSku || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#D47500' }}>
                      Return Reason:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#D47500', mb: 1 }}>
                      {returnData.return_reason || 'Incompatible or not useful for intended purpose'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Buyer Comment:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {returnData.notes || item.problem_description || 'Did not fit'}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              {/* Expandable More Information */}
              <Accordion elevation={0}>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 0, '& .MuiAccordionSummary-content': { margin: 0 } }}
                >
                  <Typography variant="body2" color="primary">
                    More information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Shipping details
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Delivery Address:</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {returnData.customer_name || 'N/A'}{returnData.customer_address ? `, ${returnData.customer_address}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Return Address</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        XS-Stock.com Ltd, Returns, 3 Brewster Place, Riverside Business Centre, Irvine, North Ayrshire KA11 5DD GB
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Carrier:</strong> {returnData.carrier || returnData.shipping_method || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Tracking ID:</strong> {returnData.tracking_number || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Tracking status:</strong> {returnData.tracking_status || 'Pending collection'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Return order details
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Total Order Value:</strong> {formatCurrency(returnData.refund_amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Refund Total Value:</strong> {formatCurrency(returnData.refund_amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Sales channel:</strong> {getPlatform() === 'Amazon' ? 'Amazon.co.uk' : getPlatform() === 'eBay' ? 'eBay.co.uk' : getPlatform()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Label Type:</strong> {returnData.label_type || getPlatform() + ' label'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>RMA:</strong> {returnData.ship_via_rma_id || returnData.return_number || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Return item details
                      </Typography>
                      {returnItems.map((item, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Order-Item ID:</strong> {item.order_item_id || item.id || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Condition:</strong> {item.condition || 'New'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Resolution:</strong> {item.resolution || 'StandardRefund'}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Action Buttons */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  sx={{ 
                    bgcolor: '#146eb4', 
                    '&:hover': { bgcolor: '#005a9e' } 
                  }}
                  onClick={() => navigate(`/returns/${id}/edit`)}
                >
                  Edit Authorisation
                </Button>
                
                <Button 
                  variant="contained"
                  sx={{ 
                    bgcolor: '#16a085', 
                    '&:hover': { bgcolor: '#138f7a' } 
                  }}
                  onClick={async () => {
                    try {
                      await axios.put(`/api/returns/${id}`, {
                        return_status: 'processed',
                        processed_date: new Date().toISOString()
                      });
                      fetchReturnDetails(); // Refresh data
                    } catch (error) {
                      console.error('Error processing refund:', error);
                      setError('Failed to process refund');
                    }
                  }}
                  disabled={returnData.return_status === 'processed'}
                >
                  Issue Refund
                </Button>
                
                <Button 
                  variant="outlined"
                  color="primary"
                  onClick={async () => {
                    try {
                      await axios.put(`/api/returns/${id}`, {
                        return_status: 'processed',
                        processed_date: new Date().toISOString()
                      });
                      fetchReturnDetails(); // Refresh data
                    } catch (error) {
                      console.error('Error completing return:', error);
                      setError('Failed to complete return');
                    }
                  }}
                  disabled={returnData.return_status === 'processed'}
                >
                  Complete
                </Button>
                
                <Button 
                  variant="outlined"
                  color="primary"
                  startIcon={<ContactIcon />}
                  onClick={() => {
                    // Handle contact buyer
                    console.log('Contact Buyer clicked');
                  }}
                >
                  Contact Buyer
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnhancedReturnDetails;